import RpcService  from '@/core/services/Rpc/RpcService';
import RpcResponse from '@/core/services/Rpc/RpcResponse';

import type RpcDataPagination from '@/core/services/Rpc/RpcDataPagination';
import type RpcDataSort       from '@/core/services/Rpc/RpcDataSort';
import type DemandV2          from '@/domains/DemandsV2/DemandV2';
import type DemandRequest     from '@/domains/DemandsV2/DemandRequest';
import type DemandOption      from '@/domains/DemandsV2/DemandOption';
import type Person            from '@/domains/Persons/Person';
import type Product           from '@/domains/Products/Product';
import type DateRange         from '@/core/types/DateRange';

/**
 * Data Access Object для работы с потребностями V2
 * Отвечает только за взаимодействие с API
 */
class DemandsV2DAO {

  /**
   * Загружает одну потребность по ID (бек включает заявки и варианты)
   */
  static async get(id: number): Promise<RpcResponse> {
    return await RpcService.makeRequest('demandsV2.get', { id });
  }

  /**
   * Загружает список потребностей с фильтрацией.
   *
   * showLoader: false — у грида свой скелетон, глобальный лоадер не нужен.
   * abortPrevious: true — при быстром переключении вкладок/фильтров
   * предыдущий запрос неактуален, лоадер обрабатывает isCancelled.
   */
  static async getList(
    filters?: {
      pattern?: string;
      isDeleted?: boolean;
      isOptionsExists?: boolean;
      isOptionsSelected?: boolean;
      isApproved?: boolean;
      isFinished?: boolean;
      category?: { id: number };
      responsible?: Person;
      desiredReceiptPeriod?: DateRange;
      product?: Product;
      buyerProductionSite?: { id: string };
    },
    pagination: RpcDataPagination | null = null,
    sort: RpcDataSort | null = null,
  ): Promise<RpcResponse> {
    return await RpcService.makeRequest(
      'demandsV2.getList',
      {
        ...filters,
        responsible: filters?.responsible ? { id: filters.responsible.id } : undefined,
        product: filters?.product ? { id: filters.product.id } : undefined,
      },
      pagination,
      sort,
      false,
      null,
      null,
      false,
      { abortPrevious: true },
    );
  }

  /**
   * Создаёт новую потребность (заявки создаются каскадом из demand.requests)
   */
  static async add(demandV2: DemandV2): Promise<RpcResponse> {
    return await RpcService.makeRequest('demandsV2.add', { demand: demandV2 });
  }

  /**
   * Обновляет потребность. Заявки внутри demand.requests бек на update игнорирует —
   * в режиме редактирования они управляются методами *Request
   */
  static async update(demandV2: DemandV2, reasonText?: string): Promise<RpcResponse> {
    return await RpcService.makeRequest('demandsV2.update', { demand: demandV2, reasonText });
  }

  /**
   * Помечает потребность удалённой
   */
  static async delete(id: number, reasonText?: string): Promise<RpcResponse> {
    return await RpcService.makeRequest('demandsV2.delete', { id, reasonText });
  }

  /**
   * Создаёт заявку. Бек НЕ возвращает id созданной сущности (возвращает 1)
   */
  static async addRequest(request: DemandRequest): Promise<RpcResponse> {
    return await RpcService.makeRequest('demandsV2.addRequest', { request });
  }

  /**
   * Обновляет заявку. Внимание: на беке нет проверки версии для заявок
   */
  static async updateRequest(request: DemandRequest): Promise<RpcResponse> {
    return await RpcService.makeRequest('demandsV2.updateRequest', { request });
  }

  /**
   * Помечает заявку удалённой
   */
  static async deleteRequest(id: number): Promise<RpcResponse> {
    return await RpcService.makeRequest('demandsV2.deleteRequest', { id });
  }

  /**
   * Создаёт вариант. Бек НЕ возвращает id созданной сущности (возвращает 1).
   * qtySelected на создании бек игнорирует и пишет 0
   */
  static async addOption(option: DemandOption): Promise<RpcResponse> {
    return await RpcService.makeRequest('demandsV2.addOption', { option });
  }

  /**
   * Обновляет вариант (в том числе qtySelected — «выбор» варианта идёт через update)
   */
  static async updateOption(option: DemandOption): Promise<RpcResponse> {
    return await RpcService.makeRequest('demandsV2.updateOption', { option });
  }

  /**
   * Помечает вариант удалённым
   */
  static async deleteOption(id: number): Promise<RpcResponse> {
    return await RpcService.makeRequest('demandsV2.deleteOption', { id });
  }
}

export default DemandsV2DAO;
